import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/api';
import logger from '../utils/logger';

export type UploadJobStatus = 'queued' | 'processing' | 'completed' | 'failed';

export interface UploadJob {
  _id: string;
  filename: string;
  account: string;
  user_email: string;
  user_uid?: string | null;
  status: UploadJobStatus;
  progress?: number;
  message?: string;
  created_at?: string;
  updated_at?: string;
  acknowledged_at?: string | null;
  total_processed?: number;
  total_successful?: number;
  total_failed?: number;
}

export interface LeadUploadJob extends UploadJob {}

export type ValidationJobStatus =
  | 'queued'
  | 'uploading'
  | 'preparing'
  | 'ready'
  | 'verifying'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface ValidationJob {
  _id: string;
  status: ValidationJobStatus | string;
  account?: string | null;
  account_scope?: string;
  message?: string | null;
  progress?: number;
  requested_count?: number;
  processed_count?: number;
  deliverable_count?: number;
  undeliverable_count?: number;
  accept_all_count?: number;
  unknown_count?: number;
  created_at?: string;
  updated_at?: string;
  completed_at?: string | null;
  acknowledged_at?: string | null;
}

export type LinkedInSyncJobStatus = 'queued' | 'processing' | 'completed' | 'failed';

export interface LinkedInSyncJob {
  _id: string;
  job_type: string;
  account?: string;
  organization_id?: string;
  status: LinkedInSyncJobStatus | string;
  message?: string | null;
  progress?: number | null;
  posts_processed?: number | null;
  posts_stored?: number | null;
  posts_missing_stats?: number | null;
  total_posts_fetched?: number | null;
  created_at?: string;
  updated_at?: string;
  completed_at?: string | null;
  acknowledged_at?: string | null;
}

export interface CredentialExpiryItem {
  account?: string;
  channel: string;
  credential_type?: string;
  expires_at?: string | null;
  access_token_days_remaining?: number | null;
  refresh_token_expires_at?: string | null;
  refresh_token_days_remaining?: number | null;
  urgency?: 'expired' | 'critical' | 'warning' | 'normal' | 'healthy';
  minimum_days_remaining?: number;
}

interface UploadJobsContextValue {
  uploads: UploadJob[];
  leadUploads: LeadUploadJob[];
  validationJobs: ValidationJob[];
  linkedinSyncJobs: LinkedInSyncJob[];
  expiringCredentials: CredentialExpiryItem[];
  unseenCount: number;
  isPolling: boolean;
  refreshNotifications: () => Promise<void>;
  acknowledgeUpload: (uploadId: string) => Promise<void>;
  acknowledgeLeadUpload: (uploadId: string) => Promise<void>;
  acknowledgeValidationJob: (jobId: string) => Promise<void>;
  acknowledgeLinkedInSyncJob: (jobId: string) => Promise<void>;
}

const UploadJobsContext = createContext<UploadJobsContextValue | undefined>(undefined);

const POLL_INTERVAL_MS = 5000;
const FINAL_UPLOAD_STATUSES = new Set<UploadJobStatus>(['completed', 'failed']);
const FINAL_VALIDATION_STATUSES = new Set(['completed', 'failed', 'cancelled']);
const FINAL_LINKEDIN_SYNC_STATUSES = new Set<LinkedInSyncJobStatus>(['completed', 'failed']);
const MAX_UPLOAD_ITEMS = 15;
const MAX_VALIDATION_ITEMS = 10;
const MAX_LINKEDIN_SYNC_ITEMS = 10;
const SSE_REPLAY_LIMIT = 50;
const SSE_RECONNECT_BASE_MS = 1000;
const SSE_RECONNECT_MAX_MS = 30000;

type JobEventCategory = 'upload' | 'validation' | 'linkedin_sync' | 'lead';

interface JobEventPayload {
  category?: JobEventCategory;
  job_id?: string;
  job_type?: string;
  status?: string;
  message?: string;
  account?: string;
  organization_id?: string;
  account_scope?: string;
  filename?: string;
  user_email?: string;
  user_uid?: string | null;
  progress?: number;
  acknowledged_at?: string | null;
  total_processed?: number;
  total_successful?: number;
  total_failed?: number;
  successful?: number;
  failed?: number;
  processed_count?: number;
  requested_count?: number;
  deliverable_count?: number;
  undeliverable_count?: number;
  accept_all_count?: number;
  unknown_count?: number;
  completed_at?: string | null;
  updated_at?: string;
  created_at?: string;
  posts_processed?: number;
  posts_stored?: number;
  posts_missing_stats?: number;
  total_posts_fetched?: number;
}

type PartialUploadJob = Partial<UploadJob> & { _id: string };
type PartialValidationJob = Partial<ValidationJob> & { _id: string };
type PartialLinkedInSyncJob = Partial<LinkedInSyncJob> & { _id: string };

const normalizeUploadStatus = (status?: string): UploadJobStatus => {
  if (status === 'queued' || status === 'processing' || status === 'completed' || status === 'failed') {
    return status;
  }
  return 'processing';
};

const mapUploadEventPayload = (payload: JobEventPayload): PartialUploadJob | null => {
  if (!payload.job_id) {
    return null;
  }

  const partial: PartialUploadJob = {
    _id: payload.job_id,
  };

  if (payload.status) partial.status = normalizeUploadStatus(payload.status);
  if (payload.message !== undefined) partial.message = payload.message;
  if (payload.account !== undefined) partial.account = payload.account;
  if (payload.filename !== undefined) partial.filename = payload.filename;
  if (payload.user_email !== undefined) partial.user_email = payload.user_email;
  if (payload.user_uid !== undefined) partial.user_uid = payload.user_uid;
  if (payload.progress !== undefined) partial.progress = payload.progress;
  if (payload.acknowledged_at !== undefined) partial.acknowledged_at = payload.acknowledged_at;
  if (payload.total_processed !== undefined) partial.total_processed = payload.total_processed;
  if (payload.total_successful !== undefined || payload.successful !== undefined) {
    partial.total_successful = payload.total_successful ?? payload.successful;
  }
  if (payload.total_failed !== undefined || payload.failed !== undefined) {
    partial.total_failed = payload.total_failed ?? payload.failed;
  }
  if (payload.updated_at !== undefined) partial.updated_at = payload.updated_at;
  if (payload.created_at !== undefined) partial.created_at = payload.created_at;

  return partial;
};

const mapValidationEventPayload = (payload: JobEventPayload): PartialValidationJob | null => {
  if (!payload.job_id) {
    return null;
  }

  const partial: PartialValidationJob = {
    _id: payload.job_id,
  };

  if (payload.status) partial.status = payload.status;
  if (payload.message !== undefined) partial.message = payload.message;
  if (payload.account !== undefined) partial.account = payload.account;
  if (payload.account_scope !== undefined) partial.account_scope = payload.account_scope;
  if (payload.progress !== undefined) partial.progress = payload.progress;
  if (payload.requested_count !== undefined) partial.requested_count = payload.requested_count;
  if (payload.processed_count !== undefined) partial.processed_count = payload.processed_count;
  if (payload.deliverable_count !== undefined) partial.deliverable_count = payload.deliverable_count;
  if (payload.undeliverable_count !== undefined) partial.undeliverable_count = payload.undeliverable_count;
  if (payload.accept_all_count !== undefined) partial.accept_all_count = payload.accept_all_count;
  if (payload.unknown_count !== undefined) partial.unknown_count = payload.unknown_count;
  if (payload.acknowledged_at !== undefined) partial.acknowledged_at = payload.acknowledged_at;
  if (payload.updated_at !== undefined) partial.updated_at = payload.updated_at;
  if (payload.created_at !== undefined) partial.created_at = payload.created_at;
  if (payload.completed_at !== undefined) partial.completed_at = payload.completed_at;

  return partial;
};

const mapLinkedInSyncEventPayload = (payload: JobEventPayload): PartialLinkedInSyncJob | null => {
  if (!payload.job_id) {
    return null;
  }

  const partial: PartialLinkedInSyncJob = {
    _id: payload.job_id,
  };

  if (payload.status) partial.status = payload.status as LinkedInSyncJobStatus;
  if (payload.message !== undefined) partial.message = payload.message;
  if (payload.account !== undefined) partial.account = payload.account;
  if (payload.organization_id !== undefined) partial.organization_id = payload.organization_id;
  if (payload.job_type !== undefined) partial.job_type = payload.job_type;
  if (payload.progress !== undefined) partial.progress = payload.progress;
  if (payload.posts_processed !== undefined) partial.posts_processed = payload.posts_processed;
  if (payload.posts_stored !== undefined) partial.posts_stored = payload.posts_stored;
  if (payload.posts_missing_stats !== undefined) partial.posts_missing_stats = payload.posts_missing_stats;
  if (payload.total_posts_fetched !== undefined) partial.total_posts_fetched = payload.total_posts_fetched;
  if (payload.acknowledged_at !== undefined) partial.acknowledged_at = payload.acknowledged_at;
  if (payload.updated_at !== undefined) partial.updated_at = payload.updated_at;
  if (payload.created_at !== undefined) partial.created_at = payload.created_at;
  if (payload.completed_at !== undefined) partial.completed_at = payload.completed_at;

  return partial;
};

const mergeRecords = <T extends { _id: string }>(current: T, incoming: Partial<T>): T => {
  const merged = { ...current } as T;
  Object.entries(incoming).forEach(([key, value]) => {
    if (value !== undefined) {
      (merged as Record<string, unknown>)[key] = value;
    }
  });
  return merged;
};

const createUploadJobFromPartial = (partial: PartialUploadJob): UploadJob => ({
  _id: partial._id,
  filename: partial.filename ?? '',
  account: partial.account ?? '',
  user_email: partial.user_email ?? '',
  user_uid: partial.user_uid ?? null,
  status: partial.status ?? 'processing',
  progress: partial.progress,
  message: partial.message,
  created_at: partial.created_at,
  updated_at: partial.updated_at,
  acknowledged_at: partial.acknowledged_at ?? null,
  total_processed: partial.total_processed,
  total_successful: partial.total_successful,
  total_failed: partial.total_failed,
});

const createValidationJobFromPartial = (partial: PartialValidationJob): ValidationJob => ({
  _id: partial._id,
  status: partial.status ?? 'queued',
  account: partial.account ?? partial.account_scope ?? '',
  account_scope: partial.account_scope,
  message: partial.message,
  progress: partial.progress,
  requested_count: partial.requested_count,
  processed_count: partial.processed_count,
  deliverable_count: partial.deliverable_count,
  undeliverable_count: partial.undeliverable_count,
  accept_all_count: partial.accept_all_count,
  unknown_count: partial.unknown_count,
  created_at: partial.created_at,
  updated_at: partial.updated_at,
  completed_at: partial.completed_at,
  acknowledged_at: partial.acknowledged_at ?? null,
});

const createLinkedInSyncJobFromPartial = (partial: PartialLinkedInSyncJob): LinkedInSyncJob => ({
  _id: partial._id,
  job_type: partial.job_type ?? 'organic_posts',
  account: partial.account ?? '',
  organization_id: partial.organization_id,
  status: (partial.status as LinkedInSyncJobStatus) ?? 'processing',
  message: partial.message,
  progress: partial.progress ?? null,
  posts_processed: partial.posts_processed ?? null,
  posts_stored: partial.posts_stored ?? null,
  posts_missing_stats: partial.posts_missing_stats ?? null,
  total_posts_fetched: partial.total_posts_fetched ?? null,
  created_at: partial.created_at,
  updated_at: partial.updated_at,
  completed_at: partial.completed_at ?? null,
  acknowledged_at: partial.acknowledged_at ?? null,
});

const deriveLatestTimestamp = (
  uploadItems: UploadJob[],
  leadUploadItems: LeadUploadJob[],
  validationItems: ValidationJob[],
  linkedinItems: LinkedInSyncJob[]
): string | null => {
  const timestamps = [...uploadItems, ...leadUploadItems, ...validationItems, ...linkedinItems]
    .map((item) => (item.updated_at ? Date.parse(item.updated_at) : Number.NaN))
    .filter((value) => Number.isFinite(value));

  if (!timestamps.length) {
    return null;
  }

  return new Date(Math.max(...timestamps)).toISOString();
};

interface ProviderProps {
  children: ReactNode;
  enabled: boolean;
}

export const UploadJobsProvider = ({ children, enabled }: ProviderProps) => {
  const [uploads, setUploads] = useState<UploadJob[]>([]);
  const [leadUploads, setLeadUploads] = useState<LeadUploadJob[]>([]);
  const [validationJobs, setValidationJobs] = useState<ValidationJob[]>([]);
  const [linkedinSyncJobs, setLinkedInSyncJobs] = useState<LinkedInSyncJob[]>([]);
  const [expiringCredentials, setExpiringCredentials] = useState<CredentialExpiryItem[]>([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const [sseState, setSseState] = useState<'idle' | 'connecting' | 'open' | 'error'>('idle');
  const abortRef = useRef(false);
  const sseControllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const lastEventTimestampRef = useRef<string | null>(null);
  const credentialPollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (sseControllerRef.current) {
        sseControllerRef.current.abort();
        sseControllerRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (credentialPollIntervalRef.current) {
        window.clearInterval(credentialPollIntervalRef.current);
        credentialPollIntervalRef.current = null;
      }
    };
  }, []);

  const authReady = apiService.isAuthenticated();

  const cleanupSseResources = useCallback(() => {
    if (sseControllerRef.current) {
      sseControllerRef.current.abort();
      sseControllerRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    if (!enabled || !apiService.isAuthenticated()) {
      setUploads([]);
      setLeadUploads([]);
      setValidationJobs([]);
      setLinkedInSyncJobs([]);
      setUnseenCount(0);
      return;
    }

    setIsPolling(true);
    try {
      // Use Promise.allSettled to handle partial failures gracefully
      // Some users may not have permissions for certain notification types
      const [uploadResult, leadUploadResult, validationResult, linkedinResult] = await Promise.allSettled([
        apiService.listUploadStatuses({ limit: 15, include_acknowledged: false }),
        apiService.getLeadUploadJobs({ limit: MAX_UPLOAD_ITEMS, include_acknowledged: false }),
        apiService.listValidationJobs({ limit: 10, include_acknowledged: false }),
        apiService.listLinkedInSyncJobs({ limit: MAX_LINKEDIN_SYNC_ITEMS, include_acknowledged: false })
      ]);

      if (abortRef.current) return;

      // Extract successful responses, use empty arrays for failures (e.g., 403 permission errors)
      const uploadItems = uploadResult.status === 'fulfilled' ? (uploadResult.value?.uploads ?? []) : [];
      const leadUploadItems = leadUploadResult.status === 'fulfilled' ? (leadUploadResult.value?.jobs ?? []) : [];
      const validationItems = validationResult.status === 'fulfilled' ? (validationResult.value?.jobs ?? []) : [];
      const linkedinItems = linkedinResult.status === 'fulfilled' ? (linkedinResult.value?.jobs ?? []) : [];

      setUploads(uploadItems);
      setLeadUploads(leadUploadItems);
      setValidationJobs(validationItems);
      setLinkedInSyncJobs(linkedinItems);

      const latestTimestamp = deriveLatestTimestamp(uploadItems, leadUploadItems, validationItems, linkedinItems);
      if (latestTimestamp) {
        lastEventTimestampRef.current = latestTimestamp;
      }
    } catch (error) {
      logger.error('Failed to fetch job notifications', error);
    } finally {
      if (!abortRef.current) {
        setIsPolling(false);
      }
    }
  }, [enabled]);

  // Fetch expiring credentials (polled separately from jobs, less frequently)
  const fetchExpiringCredentials = useCallback(async () => {
    if (!enabled || !apiService.isAuthenticated()) {
      setExpiringCredentials([]);
      return;
    }

    try {
      const response = await apiService.getExpiringCredentialsNotifications(30);
      if (abortRef.current) return;
      
      // Calculate minimum days remaining for each credential
      const credentialsWithUrgency = (response?.credentials ?? []).map((cred: CredentialExpiryItem) => {
        const accessDays = cred.access_token_days_remaining;
        const refreshDays = cred.refresh_token_days_remaining;
        let minDays = null;
        
        if (accessDays !== null && accessDays !== undefined && refreshDays !== null && refreshDays !== undefined) {
          minDays = Math.min(accessDays, refreshDays);
        } else if (accessDays !== null && accessDays !== undefined) {
          minDays = accessDays;
        } else if (refreshDays !== null && refreshDays !== undefined) {
          minDays = refreshDays;
        }
        
        return {
          ...cred,
          minimum_days_remaining: minDays
        };
      });
      
      setExpiringCredentials(credentialsWithUrgency);
    } catch (error) {
      // Silently fail if user doesn't have permission
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status !== 403) {
          logger.error('Failed to fetch expiring credentials', error);
        }
      }
    }
  }, [enabled]);

  const acknowledgeUpload = useCallback(
    async (uploadId: string) => {
      if (!enabled) return;
      try {
        await apiService.acknowledgeUploadStatus(uploadId);
        await fetchJobs();
      } catch (error) {
        logger.error('Failed to acknowledge upload job', error);
        throw error;
      }
    },
    [enabled, fetchJobs]
  );

  const acknowledgeLeadUpload = useCallback(
    async (uploadId: string) => {
      if (!enabled) return;
      try {
        setLeadUploads((current) => current.filter((job) => job._id !== uploadId));
        await apiService.acknowledgeLeadUploadJob(uploadId);
        void fetchJobs();
      } catch (error) {
        await fetchJobs();
        logger.error('Failed to acknowledge lead upload job', error);
        throw error;
      }
    },
    [enabled, fetchJobs]
  );

  const acknowledgeValidationJob = useCallback(
    async (jobId: string) => {
      if (!enabled) return;
      try {
        await apiService.acknowledgeValidationJob(jobId);
        await fetchJobs();
      } catch (error) {
        logger.error('Failed to acknowledge validation job', error);
        throw error;
      }
    },
    [enabled, fetchJobs]
  );

  const acknowledgeLinkedInSyncJob = useCallback(
    async (jobId: string) => {
      if (!enabled) return;
      try {
        await apiService.acknowledgeLinkedInSyncJob(jobId);
        await fetchJobs();
      } catch (error) {
        logger.error('Failed to acknowledge LinkedIn sync job', error);
        throw error;
      }
    },
    [enabled, fetchJobs]
  );

  const refreshNotifications = useCallback(async () => {
    await fetchJobs();
    await fetchExpiringCredentials();
  }, [fetchJobs, fetchExpiringCredentials]);

  const applyUploadPatch = useCallback((partial: PartialUploadJob) => {
    setUploads((prev) => {
      const index = prev.findIndex((item) => item._id === partial._id);
      if (index >= 0) {
        const next = [...prev];
        next[index] = mergeRecords(next[index], partial);
        return next;
      }

      const created = createUploadJobFromPartial(partial);
      return [created, ...prev].slice(0, MAX_UPLOAD_ITEMS);
    });
  }, []);

  const applyLeadUploadPatch = useCallback((partial: PartialUploadJob) => {
    setLeadUploads((prev) => {
      const index = prev.findIndex((item) => item._id === partial._id);
      if (index >= 0) {
        const next = [...prev];
        next[index] = mergeRecords(next[index], partial);
        return next;
      }

      const created = createUploadJobFromPartial(partial);
      return [created, ...prev].slice(0, MAX_UPLOAD_ITEMS);
    });
  }, []);

  const applyValidationPatch = useCallback((partial: PartialValidationJob) => {
    setValidationJobs((prev) => {
      const index = prev.findIndex((item) => item._id === partial._id);
      if (index >= 0) {
        const next = [...prev];
        next[index] = mergeRecords(next[index], partial);
        return next;
      }

      const created = createValidationJobFromPartial(partial);
      return [created, ...prev].slice(0, MAX_VALIDATION_ITEMS);
    });
  }, []);

  const applyLinkedInSyncPatch = useCallback((partial: PartialLinkedInSyncJob) => {
    setLinkedInSyncJobs((prev) => {
      const index = prev.findIndex((item) => item._id === partial._id);
      if (index >= 0) {
        const next = [...prev];
        next[index] = mergeRecords(next[index], partial);
        return next;
      }

      const created = createLinkedInSyncJobFromPartial(partial);
      return [created, ...prev].slice(0, MAX_LINKEDIN_SYNC_ITEMS);
    });
  }, []);

  const handleJobEventPayload = useCallback(
    (payload: JobEventPayload) => {
      const updatedAt = payload.updated_at ?? new Date().toISOString();
      lastEventTimestampRef.current = updatedAt;

      if (payload.category === 'upload') {
        const partial = mapUploadEventPayload(payload);
        if (partial) {
          applyUploadPatch(partial);
        }
        return;
      }

      if (payload.category === 'lead') {
        const partial = mapUploadEventPayload(payload);
        if (partial) {
          applyLeadUploadPatch(partial);
        }
        return;
      }

      if (payload.category === 'validation') {
        const partial = mapValidationEventPayload(payload);
        if (partial) {
          applyValidationPatch(partial);
        }
        return;
      }

      if (payload.category === 'linkedin_sync') {
        const partial = mapLinkedInSyncEventPayload(payload);
        if (partial) {
          applyLinkedInSyncPatch(partial);
        }
      }
    },
    [applyLeadUploadPatch, applyUploadPatch, applyValidationPatch, applyLinkedInSyncPatch]
  );

  const buildStreamUrl = useCallback(() => {
    const base = apiService.getBaseUrl();
    const normalizedBase = base ? base.replace(/\/$/, '') : '';
    return `${normalizedBase}/prospects/job-events/stream`;
  }, []);

  useEffect(() => {
    if (!enabled || !authReady) {
      cleanupSseResources();
      setSseState('idle');
      return;
    }

    let disposed = false;

    const scheduleReconnect = (attempt: number) => {
      if (disposed || abortRef.current) {
        return;
      }
      const delay = Math.min(SSE_RECONNECT_MAX_MS, SSE_RECONNECT_BASE_MS * 2 ** attempt);
      reconnectTimeoutRef.current = window.setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connect(attempt + 1);
      }, delay);
    };

    const connect = async (attempt = 0) => {
      if (disposed || abortRef.current || !enabled) {
        return;
      }

      cleanupSseResources();

      if (!apiService.isAuthenticated()) {
        scheduleReconnect(attempt);
        return;
      }

      const authHeader = apiService.getAuthHeader();
      if (!authHeader) {
        scheduleReconnect(attempt);
        return;
      }

      try {
        setSseState('connecting');
        const controller = new AbortController();
        sseControllerRef.current = controller;

        const params = new URLSearchParams();
        if (lastEventTimestampRef.current) {
          params.set('since', lastEventTimestampRef.current);
          params.set('replay_limit', String(SSE_REPLAY_LIMIT));
        }

        const url = buildStreamUrl();
        const query = params.toString();
        const streamUrl = query ? `${url}?${query}` : url;

        const response = await fetch(streamUrl, {
          method: 'GET',
          headers: {
            Authorization: authHeader,
          },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`SSE connection failed (${response.status})`);
        }

        setSseState('open');
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!disposed && !abortRef.current) {
          const { value, done } = await reader.read();
          if (done) {
            throw new Error('Stream closed');
          }

          buffer += decoder.decode(value, { stream: true });
          buffer = buffer.replace(/\r/g, '');

          let boundary = buffer.indexOf('\n\n');
          while (boundary !== -1) {
            const rawEvent = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf('\n\n');

            const trimmed = rawEvent.trim();
            if (!trimmed || trimmed.startsWith(':')) {
              continue;
            }

            let eventName = 'message';
            const dataLines: string[] = [];
            trimmed.split('\n').forEach((line) => {
              if (line.startsWith('event:')) {
                eventName = line.slice(6).trim();
              } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
              }
            });

            if (!dataLines.length) {
              continue;
            }

            const dataPayload = dataLines.join('\n');
            try {
              const parsed = JSON.parse(dataPayload) as JobEventPayload;
              if (eventName === 'error') {
                logger.warn('Job event stream error payload', parsed);
              }
              handleJobEventPayload(parsed);
            } catch (error) {
              logger.warn('Failed to parse SSE payload', error);
            }
          }
        }
      } catch (error) {
        if (disposed || abortRef.current) {
          return;
        }
        if (sseControllerRef.current?.signal.aborted) {
          return;
        }
        logger.warn('Job event stream disconnected', error);
        setSseState('error');
        scheduleReconnect(attempt);
      }
    };

    connect();

    return () => {
      disposed = true;
      cleanupSseResources();
      setSseState('idle');
    };
  }, [authReady, buildStreamUrl, cleanupSseResources, enabled, handleJobEventPayload]);

  useEffect(() => {
    if (!enabled || !authReady) {
      setUploads([]);
      setLeadUploads([]);
      setValidationJobs([]);
      setLinkedInSyncJobs([]);
      setUnseenCount(0);
      return;
    }
  }, [enabled, authReady]);

  useEffect(() => {
    if (!enabled || !authReady) {
      return;
    }

    if (sseState === 'open') {
      return;
    }

    fetchJobs();
    fetchExpiringCredentials(); // Initial fetch
    
    const intervalId = window.setInterval(fetchJobs, POLL_INTERVAL_MS);
    credentialPollIntervalRef.current = window.setInterval(fetchExpiringCredentials, 60000); // Poll every 60s
    
    return () => {
      window.clearInterval(intervalId);
      if (credentialPollIntervalRef.current) {
        window.clearInterval(credentialPollIntervalRef.current);
        credentialPollIntervalRef.current = null;
      }
    };
  }, [authReady, enabled, fetchJobs, fetchExpiringCredentials, sseState]);

  useEffect(() => {
    if (!enabled) {
      setUnseenCount(0);
      return;
    }

    const uploadUnseen = uploads.filter(
      (job) => FINAL_UPLOAD_STATUSES.has(job.status) && !job.acknowledged_at
    ).length;
    const leadUploadUnseen = leadUploads.filter(
      (job) => FINAL_UPLOAD_STATUSES.has(job.status as UploadJobStatus) && !job.acknowledged_at
    ).length;
    const validationUnseen = validationJobs.filter((job) => {
      const status = (job.status ?? '').toString().toLowerCase();
      return !job.acknowledged_at && FINAL_VALIDATION_STATUSES.has(status);
    }).length;
    const linkedinUnseen = linkedinSyncJobs.filter((job) => {
      const status = (job.status ?? '').toString().toLowerCase() as LinkedInSyncJobStatus;
      return !job.acknowledged_at && FINAL_LINKEDIN_SYNC_STATUSES.has(status);
    }).length;

    setUnseenCount(uploadUnseen + leadUploadUnseen + validationUnseen + linkedinUnseen);
  }, [enabled, uploads, leadUploads, validationJobs, linkedinSyncJobs]);

  const value = useMemo(
    () => ({
      uploads,
      leadUploads,
      validationJobs,
      linkedinSyncJobs,
      expiringCredentials,
      unseenCount,
      isPolling,
      refreshNotifications,
      acknowledgeUpload,
      acknowledgeLeadUpload,
      acknowledgeValidationJob,
      acknowledgeLinkedInSyncJob
    }),
    [
      uploads,
      leadUploads,
      validationJobs,
      linkedinSyncJobs,
      expiringCredentials,
      unseenCount,
      isPolling,
      refreshNotifications,
      acknowledgeUpload,
      acknowledgeLeadUpload,
      acknowledgeValidationJob,
      acknowledgeLinkedInSyncJob
    ]
  );

  return <UploadJobsContext.Provider value={value}>{children}</UploadJobsContext.Provider>;
};

export const useUploadJobs = () => {
  const context = useContext(UploadJobsContext);
  if (!context) {
    throw new Error('useUploadJobs must be used within an UploadJobsProvider');
  }
  return context;
};
